import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import {
  computeEyeAspectRatios,
  computeIPD,
  earLooksPlausible,
  packLandmarks,
  poseFromMatrix,
  RECORDED_LANDMARKS,
  ZERO_POSE,
} from "../core/landmarks.ts";
import type { EyePair, SignalFrame } from "../core/types.ts";
import type { FrameSource } from "./FrameSource.ts";

/** Both served from our own origin — see vite.config.ts and public/_headers. */
const WASM_PATH = "/mediapipe/wasm";
const MODEL_PATH = "/mediapipe/face_landmarker.task";

export interface CameraOptions {
  video: HTMLVideoElement;
  /**
   * Inference rate. 15 is the spec's target — a blink lasts 100–400ms so 15fps
   * nominally captures it — but that leaves only 1.5–6 samples per blink, which
   * is thin for locating the amplitude peak that partial/complete classification
   * depends on. Configurable precisely so the eval harness can measure what
   * 15fps actually costs against 24 and 30 instead of assuming.
   */
  targetFps?: number;
  requestedWidth?: number;
  requestedHeight?: number;
  /**
   * Longest edge of the image actually handed to the model, in pixels.
   *
   * Spec §5 calls for downscaling before inference and this was skipped in the
   * first pass, which turned out to matter: an iPhone requesting 24fps achieved
   * 14.9, running the full 720x1280 frame through the model every time. That is
   * ~7x more pixels than the landmarker needs, and the shortfall lands squarely
   * on amplitude accuracy, since fewer samples per blink means the peak is
   * missed more often.
   *
   * Landmarks come back normalised to the image, so downscaling costs nothing in
   * coordinate accuracy — the values are identical fractions either way, and
   * pixel-space conversion still uses the full video dimensions.
   */
  inferenceMaxEdge?: number;
  onDiagnostic?: (message: string) => void;
}

export interface CameraInfo {
  width: number;
  height: number;
  delegate: "GPU" | "CPU";
  /** Frame rate the camera actually granted, which is rarely the one asked for. */
  streamFps: number;
  label: string;
  /** Frames per second the camera actually delivers, before we throttle. */
  sourceFps: number;
  /** Size actually fed to the model, after downscaling. */
  inferenceSize: string;
  /** Rolling mean cost of one detectForVideo call. The real frame-rate ceiling. */
  inferenceMs: number;
}

interface VideoFrameMeta {
  mediaTime: number;
  presentedFrames: number;
}

interface ResolvedOptions {
  video: HTMLVideoElement;
  targetFps: number;
  requestedWidth: number;
  requestedHeight: number;
  inferenceMaxEdge: number;
  onDiagnostic: ((message: string) => void) | undefined;
}

type VideoWithRVFC = HTMLVideoElement & {
  requestVideoFrameCallback?(callback: (now: number, metadata: VideoFrameMeta) => void): number;
  cancelVideoFrameCallback?(handle: number): void;
};

/**
 * Camera → MediaPipe → SignalFrame. The only DOM-bound source, and the only
 * place in the project that ever touches a pixel.
 *
 * Frames are consumed for inference and dropped. Nothing is buffered, nothing is
 * encoded, nothing is stored — the SignalFrames that leave here are numbers.
 */
export class CameraFrameSource implements FrameSource {
  readonly kind = "camera" as const;

  readonly #options: ResolvedOptions;
  readonly #frameHandlers = new Set<(frame: SignalFrame) => void>();
  readonly #endHandlers = new Set<() => void>();

  #landmarker: FaceLandmarker | null = null;
  #stream: MediaStream | null = null;
  #rafHandle: number | null = null;
  #rvfcHandle: number | null = null;
  #running = false;

  #blendshapeIndex: Map<string, number> | null = null;
  #originT: number | null = null;
  #lastInferenceT = -1;
  #nextDeadline = -1;
  #sourceFrames = 0;
  #sourceFirstT = -1;
  #lastEmittedT = -1;
  #validatedEar = false;
  #scratch: HTMLCanvasElement | null = null;
  #scratchCtx: CanvasRenderingContext2D | null = null;
  #inferenceMs = 0;
  #clock: "undecided" | "media" | "callback" = "undecided";
  #clockSamples = 0;
  #firstMediaTime: number | null = null;

  #info: CameraInfo | null = null;

  constructor(options: CameraOptions) {
    this.#options = {
      video: options.video,
      targetFps: options.targetFps ?? 15,
      requestedWidth: options.requestedWidth ?? 1280,
      requestedHeight: options.requestedHeight ?? 720,
      inferenceMaxEdge: options.inferenceMaxEdge ?? 480,
      onDiagnostic: options.onDiagnostic,
    };
  }

  get info(): CameraInfo | null {
    return this.#info;
  }

  onFrame(handler: (frame: SignalFrame) => void): () => void {
    this.#frameHandlers.add(handler);
    return () => this.#frameHandlers.delete(handler);
  }

  onEnd(handler: () => void): () => void {
    this.#endHandlers.add(handler);
    return () => this.#endHandlers.delete(handler);
  }

  async start(): Promise<void> {
    if (this.#running) return;

    const { video, requestedWidth, requestedHeight, targetFps } = this.#options;

    this.#stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "user",
        width: { ideal: requestedWidth },
        height: { ideal: requestedHeight },
        frameRate: { ideal: Math.max(targetFps, 30) },
      },
      audio: false,
    });

    video.srcObject = this.#stream;
    await video.play();
    await waitForDimensions(video);

    const delegate = await this.#createLandmarker();

    const scratch = this.#prepareScratch(video.videoWidth, video.videoHeight);

    const track = this.#stream.getVideoTracks()[0];
    const settings = track?.getSettings() ?? {};
    this.#info = {
      width: video.videoWidth,
      height: video.videoHeight,
      delegate,
      streamFps: settings.frameRate ?? Number.NaN,
      label: track?.label ?? "unknown",
      sourceFps: Number.NaN,
      inferenceSize: `${scratch.width}x${scratch.height}`,
      inferenceMs: 0,
    };

    this.#running = true;
    this.#originT = null;
    this.#lastInferenceT = -1;
    this.#nextDeadline = -1;
    this.#sourceFrames = 0;
    this.#sourceFirstT = -1;
    this.#lastEmittedT = -1;
    this.#validatedEar = false;
    this.#clock = "undecided";
    this.#clockSamples = 0;
    this.#firstMediaTime = null;
    this.#scheduleFrame();
  }

  async stop(): Promise<void> {
    this.#running = false;

    const video = this.#options.video as VideoWithRVFC;
    if (this.#rvfcHandle !== null) {
      video.cancelVideoFrameCallback?.(this.#rvfcHandle);
      this.#rvfcHandle = null;
    }
    if (this.#rafHandle !== null) {
      cancelAnimationFrame(this.#rafHandle);
      this.#rafHandle = null;
    }

    for (const track of this.#stream?.getTracks() ?? []) track.stop();
    this.#stream = null;
    this.#options.video.srcObject = null;

    this.#landmarker?.close();
    this.#landmarker = null;

    for (const handler of this.#endHandlers) handler();
  }

  // -------------------------------------------------------------------------

  /**
   * Allocates the downscale target once, preserving aspect ratio.
   *
   * Never upscales: if the camera already delivers something small, feeding the
   * model a stretched copy would cost time and add nothing.
   */
  #prepareScratch(width: number, height: number): HTMLCanvasElement {
    const longest = Math.max(width, height);
    const scale = Math.min(1, this.#options.inferenceMaxEdge / longest);

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    this.#scratch = canvas;
    this.#scratchCtx = canvas.getContext("2d", { alpha: false, willReadFrequently: false });
    return canvas;
  }

  async #createLandmarker(): Promise<"GPU" | "CPU"> {
    const fileset = await FilesetResolver.forVisionTasks(WASM_PATH);

    const build = async (delegate: "GPU" | "CPU") =>
      FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_PATH, delegate },
        runningMode: "VIDEO",
        numFaces: 1,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
        minFaceDetectionConfidence: 0.5,
        minFacePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

    try {
      this.#landmarker = await build("GPU");
      return "GPU";
    } catch (err) {
      this.#diagnose(`GPU delegate unavailable, falling back to CPU: ${describe(err)}`);
      this.#landmarker = await build("CPU");
      return "CPU";
    }
  }

  /**
   * Prefers `requestVideoFrameCallback`, which fires once per *decoded* frame and
   * hands over that frame's own presentation timestamp. `requestAnimationFrame`
   * is tied to display refresh instead, so it both re-processes unchanged frames
   * and timestamps them by when the handler happened to run — which would put
   * display jitter straight into every latency measurement.
   */
  #scheduleFrame(): void {
    if (!this.#running) return;
    const video = this.#options.video as VideoWithRVFC;

    if (typeof video.requestVideoFrameCallback === "function") {
      this.#rvfcHandle = video.requestVideoFrameCallback((now, metadata) => {
        this.#onVideoFrame(this.#timeFor(now, metadata));
        this.#scheduleFrame();
      });
      return;
    }

    this.#rafHandle = requestAnimationFrame((now) => {
      this.#onVideoFrame(now);
      this.#scheduleFrame();
    });
  }

  /**
   * Picks a frame clock, preferring the video's own presentation timestamp.
   *
   * `mediaTime` is the better signal in principle — it is when the frame was
   * captured, not when our callback happened to run, so it keeps display jitter
   * out of every latency measurement. But for a live MediaStream some browsers
   * leave it pinned at zero. Since VIDEO-mode inference requires strictly
   * increasing timestamps, a frozen clock means every frame after the first is
   * silently dropped and the app looks like it simply cannot see blinks.
   *
   * So: use it, but verify it moves, and fall back permanently if it does not.
   */
  #timeFor(now: number, metadata: VideoFrameMeta): number {
    if (this.#clock === "callback") return now;

    const mediaMs = metadata.mediaTime * 1000;
    if (this.#clock === "media") return mediaMs;

    // Undecided: watch the first few frames before committing.
    this.#clockSamples++;
    if (this.#firstMediaTime === null) this.#firstMediaTime = mediaMs;

    if (mediaMs > this.#firstMediaTime + 1) {
      this.#clock = "media";
      return mediaMs;
    }
    if (this.#clockSamples >= 5) {
      this.#clock = "callback";
      this.#diagnose(
        "video mediaTime is not advancing on this browser — falling back to the " +
          "callback clock. Latency figures will carry a little display jitter.",
      );
      return now;
    }
    return mediaMs;
  }

  #onVideoFrame(rawT: number): void {
    const landmarker = this.#landmarker;
    if (!landmarker || !this.#running) return;

    this.#originT ??= rawT;
    const t = rawT - this.#originT;

    // Counted before the throttle, so "the camera is slow" and "we are dropping
    // frames" stop being indistinguishable — which is exactly what let a
    // decimation bug masquerade as a hardware limit across nine sessions.
    if (this.#sourceFirstT < 0) this.#sourceFirstT = t;
    this.#sourceFrames++;
    if (this.#info && t > this.#sourceFirstT) {
      this.#info.sourceFps = ((this.#sourceFrames - 1) * 1000) / (t - this.#sourceFirstT);
    }

    // Drop to the target rate. Running at 30 when 15 will do halves the battery
    // cost for a signal that, per spec §5, does not need the extra frames.
    // Deadline accumulator, NOT a minimum gap between frames.
    //
    // The minimum-gap version halved the capture rate, and did it silently. A
    // 24fps target sets a 41.7ms interval; the guard rejected anything arriving
    // sooner than 37.5ms; a 30fps camera delivers every 33.3ms. Every second
    // frame was therefore discarded, giving exactly 15fps — which is the 14.9
    // that showed up in every single recorded session and got blamed first on
    // slow inference and then on dim light. It was neither.
    //
    // Accumulating deadlines instead drops only the frames genuinely surplus to
    // the target: from a 30fps source it keeps roughly 4 in 5, landing near 24
    // rather than falling back to half rate.
    const interval = 1000 / this.#options.targetFps;
    if (this.#nextDeadline < 0) this.#nextDeadline = t;
    if (t < this.#nextDeadline) return;
    // Clamp forward so a stall does not queue up a burst of catch-up frames.
    this.#nextDeadline = Math.max(this.#nextDeadline + interval, t - interval);

    // MediaPipe's VIDEO mode requires strictly increasing timestamps and throws
    // otherwise; mediaTime can repeat if the same frame is presented twice.
    if (t <= this.#lastInferenceT) return;
    this.#lastInferenceT = t;

    const video = this.#options.video;
    const scratch = this.#scratch;
    const ctx = this.#scratchCtx;

    let result: ReturnType<FaceLandmarker["detectForVideo"]>;
    const startedAt = performance.now();
    try {
      // The model sees the downscaled copy; everything downstream still works in
      // full-resolution pixel space, because landmarks are normalised fractions
      // of whatever image was fed in.
      let source: HTMLVideoElement | HTMLCanvasElement = video;
      if (scratch && ctx && scratch.width > 0) {
        ctx.drawImage(video, 0, 0, scratch.width, scratch.height);
        source = scratch;
      }
      result = landmarker.detectForVideo(source, t);
    } catch (err) {
      this.#diagnose(`Inference failed: ${describe(err)}`);
      return;
    }

    const cost = performance.now() - startedAt;
    this.#inferenceMs = this.#inferenceMs === 0 ? cost : this.#inferenceMs * 0.9 + cost * 0.1;
    if (this.#info) this.#info.inferenceMs = this.#inferenceMs;

    const frame = this.#toSignalFrame(result, t, video.videoWidth, video.videoHeight);
    if (frame.t <= this.#lastEmittedT) return;
    this.#lastEmittedT = frame.t;

    for (const handler of this.#frameHandlers) handler(frame);
  }

  #toSignalFrame(
    result: ReturnType<FaceLandmarker["detectForVideo"]>,
    t: number,
    width: number,
    height: number,
  ): SignalFrame {
    const landmarks = result.faceLandmarks?.[0];
    if (!landmarks || landmarks.length <= Math.max(...RECORDED_LANDMARKS)) {
      return emptyFrame(t);
    }

    // Pixel space, not normalised space. MediaPipe scales x and y independently,
    // so an EAR computed on the raw values would change with the frame's aspect
    // ratio — different on a 16:9 laptop than on a portrait phone, for the same
    // eye. See computeEAR in core/landmarks.ts.
    const read = (index: number) => {
      const lm = landmarks[index];
      return lm ? { x: lm.x * width, y: lm.y * height } : { x: Number.NaN, y: Number.NaN };
    };

    const ear = computeEyeAspectRatios(read);
    this.#validateEarOnce(ear);

    const shapes = result.faceBlendshapes?.[0]?.categories;
    const blendshapes = shapes ? shapes.map((c) => c.score) : new Array<number>(52).fill(0);
    if (shapes && !this.#blendshapeIndex) {
      this.#blendshapeIndex = new Map(shapes.map((c, i) => [c.categoryName, i]));
    }

    const at = (name: string) => {
      const i = this.#blendshapeIndex?.get(name);
      return i === undefined ? 0 : (blendshapes[i] ?? 0);
    };

    const matrix = result.facialTransformationMatrixes?.[0]?.data;
    const pose = matrix ? poseFromMatrix(matrix) : ZERO_POSE;

    return {
      t,
      faceFound: true,
      ear,
      blink: { left: at("eyeBlinkLeft"), right: at("eyeBlinkRight") },
      squint: { left: at("eyeSquintLeft"), right: at("eyeSquintRight") },
      pose,
      ipd: computeIPD(read),
      lowConfidence: false,
      eyeLandmarks: packLandmarks(read),
      blendshapes,
    };
  }

  /**
   * Spec §2 is explicit that the landmark indices must be checked at runtime
   * rather than trusted from memory. A wrong table yields a plausible-looking
   * number rather than an error, so without this the failure is silent and shows
   * up only as an inexplicably bad detector.
   */
  #validateEarOnce(ear: EyePair<number>): void {
    if (this.#validatedEar) return;
    this.#validatedEar = true;
    for (const [side, value] of Object.entries(ear)) {
      if (!earLooksPlausible(value)) {
        this.#diagnose(
          `${side} eye EAR is ${value.toFixed(3)}, outside the plausible 0.02–0.9 range — ` +
            "the landmark index table in core/landmarks.ts is probably wrong.",
        );
      }
    }
  }

  #diagnose(message: string): void {
    console.warn(`[camera] ${message}`);
    this.#options.onDiagnostic?.(message);
  }
}

function emptyFrame(t: number): SignalFrame {
  return {
    t,
    faceFound: false,
    ear: { left: Number.NaN, right: Number.NaN },
    blink: { left: 0, right: 0 },
    squint: { left: 0, right: 0 },
    pose: ZERO_POSE,
    ipd: Number.NaN,
    lowConfidence: true,
    eyeLandmarks: [],
    blendshapes: [],
  };
}

function waitForDimensions(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth > 0) return Promise.resolve();
  return new Promise((resolve) => {
    video.addEventListener("loadeddata", () => resolve(), { once: true });
  });
}

function describe(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
